import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styles from './SegmentBar.module.scss';

/* ────────────────────────────────────────────────────────────
 * SegmentBar — a one-line proportional breakdown.
 *
 * The composition counterpart to ProgressBar: where ProgressBar
 * answers "how far along", this answers "of what is it made".
 * Sized to sit inside a table cell beside the numbers it splits.
 *
 *   <SegmentBar
 *     segments={[
 *       { key: 'male',   label: 'Male',   value: 30, color: 'var(--chart-1)' },
 *       { key: 'female', label: 'Female', value: 14, color: 'var(--chart-4)' },
 *     ]}
 *     ariaLabel="Engineering gender split: Male 30, Female 14"
 *   />
 *
 * Segments are separated by a 2px gap in the surface colour so
 * two adjacent fills never read as one. A zero-value segment is
 * dropped rather than drawn as a hairline, and an all-zero bar
 * falls back to an empty track so the row still has a shape.
 *
 * Identity must never rest on the fills alone — pair the bar
 * with labelled numbers, a legend or dotted column headers, and
 * always give it an `ariaLabel` naming each part.
 * ──────────────────────────────────────────────────────────── */

const SegmentBar = ({
  segments,
  size,
  ariaLabel,
  focusable,
  className,
}) => {
  const visible = useMemo(
    () => segments.filter((s) => Number(s.value) > 0),
    [segments]
  );

  const classNames = [
    styles.track,
    size === 'sm' ? styles.sizeSm : '',
    size === 'lg' ? styles.sizeLg : '',
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span
      className={classNames}
      role="img"
      aria-label={ariaLabel}
      tabIndex={focusable ? 0 : undefined}
    >
      {visible.map((segment, i) => (
        <span
          key={segment.key ?? segment.label ?? i}
          className={styles.segment}
          /* The proportion and the series colour are data — the
             only two values a stylesheet cannot know up front. */
          style={{
            flexGrow: Number(segment.value),
            backgroundColor: segment.color,
          }}
        />
      ))}
      {visible.length === 0 && <span className={styles.empty} />}
    </span>
  );
};

SegmentBar.propTypes = {
  segments: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
      value: PropTypes.number,
      // Any CSS colour — pass a --chart-* token for chart parity.
      color: PropTypes.string,
    })
  ),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  // Required in practice: the bar is the only thing a screen
  // reader can see here, so it has to name every part.
  ariaLabel: PropTypes.string,
  // Set when the bar carries a <Tooltip> — a tooltip that only
  // opens on hover is unreachable from the keyboard.
  focusable: PropTypes.bool,
  className: PropTypes.string,
};

SegmentBar.defaultProps = {
  segments: [],
  size: 'md',
  ariaLabel: undefined,
  focusable: false,
  className: '',
};

export default SegmentBar;
